import cloudflareAssets from "@/assets/cloudflareAssets";
import type { IDepositSchedule } from "@/modules/deposit/common/types";
import { useGetDepositScheduleQuery } from "@/modules/deposit/services";
import { INITIAL_PAGE, PageSizeOptions } from "@/shared/common/constants";
import { CloudflareAsset, ErrorMessages, TableNames } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, Select } from "@/shared/components";
import { getErrorMessage, getTableName } from "@/shared/helpers";
import { useTableHooks } from "@/shared/hooks";
import {
  AdvancedTable as Table,
  ColumnSettings
} from "@ab.uitools/ui-library/components/AdvancedTable";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { getColumns } from "./columns";

export const DepositSchedule = () => {
  const { depositId = "" } = useParams();
  const { t } = useTranslation();
  const [depositSchedule, setDepositSchedule] = useState<IDepositSchedule[]>([]);

  const {
    page,
    pageSize,
    tableRef,
    hasNextPage,
    setHasNextPage,
    onNextPage,
    onPrevPage,
    onPageSizeChange,
    updateHasNextPage,
  } = useTableHooks<IDepositSchedule>();

  const {
    data: list,
    isFetching,
    isError,
    error,
  } = useGetDepositScheduleQuery(
    {
      id: depositId,
      pageIndex: page,
      pageSize,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const columns = getColumns({ t });

  useEffect(() => {
    if (!list) return;

    if (list?.length || (!list?.length && page === INITIAL_PAGE)) {
      setDepositSchedule(list);
      updateHasNextPage(list.length);
    } else if (!list?.length && page !== INITIAL_PAGE) {
      onPrevPage();
      setHasNextPage(false);
    }
  }, [list]);

  const tableName = getTableName(TableNames.DepositSchedule);

  return (
    <div className="position-relative page-body">
      <div className="full-width flexbox justify-content--center">
        <div
          className={classNames("advanced_table-wrapper", {
            "table-full": depositSchedule.length,
            "table-empty": !depositSchedule.length,
          })}>
          <div className="full-width pb-24">
            <Heading weight="bolder" size="xsmall">
              {t("deposits.depositSchedule")}
            </Heading>
          </div>
          <Table
            data={depositSchedule || []}
            withSelect
            columns={columns}
            isLoading={isFetching || !tableName}
            hasError={isError}
            tableSettings={{
              tableName: tableName || "",
              persistColumnSettings: "localStorage"
            }}
            emptyTitle={
              isError ?
                t(getErrorMessage(error as IError, ErrorMessages.TryAgainShortly)) :
                t("general.noData")
            }
            emptySubTitle={
              !isError ? t("general.noDataAvailable") : ""
            }
            emptyIllustration={cloudflareAssets[CloudflareAsset.EmptyContent]}
            renderHeader={(table) => (
              <div className="advanced-table__header flexbox justify-content--end py-16 px-24">
                <div className="flexbox">
                  <ColumnSettings
                    table={table}
                    allToggleText={t("general.all")}
                    tooltipText={t("general.columnCantBeHidden")}
                  />
                </div>
              </div>
            )}
            renderFooter={(table) => {
              tableRef.current = table;
              return (
                <div className="advanced-table__footer">
                  <div className="advanced-table__pagination">
                    <Select
                      setSelectedValue={(value) => onPageSizeChange(value as string)}
                      selectedValue={`${pageSize}`}
                      options={PageSizeOptions}
                    />
                    <div className="flexbox align-items--center">
                      <Button
                        onClick={onPrevPage}
                        size="medium"
                        type="tertiary"
                        data-testid="deposit-schedule-prev-page"
                        data-ga-id="deposit-schedule-prev-page"
                        data-ga-action="Deposit schedule table previos page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortLeft}/>
                          ),
                        }}
                        disabled={page === INITIAL_PAGE}
                      />
                      <Button
                        onClick={onNextPage}
                        size="medium"
                        type="tertiary"
                        data-testid="deposit-schedule-next-page"
                        data-ga-id="deposit-schedule-next-page"
                        data-ga-action="Deposit schedule table next page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortRight}/>
                          ),
                        }}
                        disabled={!hasNextPage}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};
